/* eslint-disable complexity */
import { type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { AnimatePresence, motion } from "framer-motion";
import { tv, type VariantProps } from "tailwind-variants";
import { useMediaQuery, useOnClickOutside, useWindowSize } from "usehooks-ts";

import { chain, useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";

import { Button, type ButtonProps } from "../Button";
import { Popover, type PopoverProps } from "../Popover";
import { resolveDropdownPosition } from "./resolveDropdownPosition";

export const dropdownVariants = tv({
	slots: {
		trigger: "",
		popover:
			"scroll absolute z-50 max-h-[315px] w-fit overflow-y-scroll border border-secondary-border bg-secondary shadow-xl",
		base: "relative flex w-fit flex-row items-center gap-2",
	},
	variants: {
		position: {
			left: {
				popover: "mr-2",
			},
			leftTop: {
				popover: "mr-2",
			},
			leftBottom: {
				popover: "mr-2",
			},
			right: {
				popover: "ml-2",
			},
			rightTop: {
				popover: "ml-2",
			},
			rightBottom: {
				popover: "ml-2",
			},
			top: {
				popover: "mb-2",
			},
			topLeft: {
				popover: "mb-2",
			},
			topRight: {
				popover: "mb-2",
			},
			bottom: {
				popover: "mt-2",
			},
			bottomLeft: {
				popover: "mt-2",
			},
			bottomRight: {
				popover: "mt-2",
			},
		},
	},
});

type DropdownVariant = VariantProps<typeof dropdownVariants>;

export type DropdownProps = DropdownVariant & {
	readonly className?: string;
	readonly trigger: Omit<ButtonProps, "children"> & { text?: ReactNode };
	readonly popover: Omit<PopoverProps, "close">;
	readonly onClickOutside?: Parameters<typeof useOnClickOutside>[1];
	readonly isDisableCloseOnClickOutside?: boolean;
	readonly isWithBackdrop?: boolean;
	readonly isOpenWhenHovered?: boolean;
	readonly isPopoverWidthFitButton?: boolean;
	readonly isSelectable?: boolean;
	readonly scrollBehavior?: "close" | "follow";
	readonly selectedKey?: string;
	readonly rightComponent?: ReactNode | ((openDropdown: () => void) => ReactNode);
};

function getScrollParent(element: HTMLElement | undefined): HTMLElement | undefined {
	if (!element) return undefined;

	let parent = element.parentElement;

	while (parent) {
		const { overflow, overflowY } = window.getComputedStyle(parent);

		if (
			overflow === "auto" ||
			overflow === "scroll" ||
			overflowY === "auto" ||
			overflowY === "scroll"
		) {
			return parent;
		}

		parent = parent.parentElement;
	}

	return undefined;
}

export function Dropdown({
	trigger,
	popover,
	isSelectable,
	position = "bottom",
	onClickOutside,
	isDisableCloseOnClickOutside = false,
	isWithBackdrop = false,
	isOpenWhenHovered = false,
	isPopoverWidthFitButton = false,
	scrollBehavior = "close",
	className,
	selectedKey,
	rightComponent,
}: DropdownProps) {
	const { width, height } = useWindowSize();
	const [resolvedPosition, setResolvedPosition] = useState(position);
	const {
		popover: popoverClassName,
		trigger: triggerClassName,
		base,
	} = dropdownVariants({
		position: resolvedPosition,
	});

	const triggerRef = useRef<HTMLButtonElement>(null!);
	const popoverRef = useRef<HTMLDivElement>(null!);
	const scrollParentRef = useRef<HTMLElement | undefined>(null);
	const closeTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

	const selectedItem =
		isSelectable && selectedKey !== undefined
			? popover.listbox?.items.find((item) => item.key === selectedKey)
			: undefined;

	const { isOpen, toggleIsOpen, open, close } = useIsOpen();
	const canHover = useMediaQuery("(hover: hover)");
	const isHoverModeActive = isOpenWhenHovered && canHover;

	function stopPropagation(event: MouseEvent<HTMLButtonElement>) {
		event.stopPropagation();
	}

	useOnClickOutside(
		popoverRef,
		chain(onClickOutside, !isDisableCloseOnClickOutside && !isHoverModeActive && close),
	);

	useEffect(() => {
		scrollParentRef.current = getScrollParent(triggerRef.current);
	}, []);

	const updatePopoverPosition = useCallback(() => {
		if (triggerRef.current && popoverRef.current && position) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const popoverRect = popoverRef.current.getBoundingClientRect();
			// Resolved from the prop rather than from the current side, so the decision
			// is the same on every pass instead of ping-ponging between the two.
			const openedPosition = resolveDropdownPosition({
				position,
				triggerRect,
				popoverRect,
				viewportHeight: height,
			});

			setResolvedPosition(openedPosition);

			switch (openedPosition) {
				case "left": {
					popoverRef.current.style.right = `${width - triggerRect.x}px`;
					popoverRef.current.style.top = `${triggerRect.y + triggerRect.height / 2 - popoverRect.height / 2}px`;
					break;
				}

				case "leftBottom": {
					popoverRef.current.style.right = `${width - triggerRect.x}px`;
					popoverRef.current.style.top = `${triggerRect.y}px`;
					break;
				}

				case "leftTop": {
					popoverRef.current.style.right = `${width - triggerRect.x}px`;
					popoverRef.current.style.top = `${triggerRect.y - popoverRect.height + triggerRect.height}px`;
					break;
				}

				case "right": {
					popoverRef.current.style.left = `${triggerRect.x + triggerRect.width}px`;
					popoverRef.current.style.top = `${triggerRect.y + triggerRect.height / 2 - popoverRect.height / 2}px`;
					break;
				}

				case "rightBottom": {
					popoverRef.current.style.left = `${triggerRect.x + triggerRect.width}px`;
					popoverRef.current.style.top = `${triggerRect.y}px`;
					break;
				}

				case "rightTop": {
					popoverRef.current.style.left = `${triggerRect.x + triggerRect.width}px`;
					popoverRef.current.style.top = `${triggerRect.y - popoverRect.height + triggerRect.height}px`;
					break;
				}

				case "bottom": {
					popoverRef.current.style.left = `${triggerRect.x + triggerRect.width / 2 - popoverRect.width / 2}px`;
					popoverRef.current.style.top = `${triggerRect.y + triggerRect.height}px`;
					break;
				}

				case "bottomLeft": {
					popoverRef.current.style.left = `${triggerRect.x}px`;
					popoverRef.current.style.top = `${triggerRect.y + triggerRect.height}px`;
					break;
				}

				case "bottomRight": {
					popoverRef.current.style.right = `${width - triggerRect.right}px`;
					popoverRef.current.style.top = `${triggerRect.y + triggerRect.height}px`;
					break;
				}

				case "top": {
					popoverRef.current.style.top = `${triggerRect.y - popoverRect.height - triggerRect.height / 4}px`;
					popoverRef.current.style.left = `${triggerRect.x + triggerRect.width / 2 - popoverRect.width / 2}px`;
					break;
				}

				case "topLeft": {
					popoverRef.current.style.top = `${triggerRect.y - popoverRect.height - triggerRect.height / 4}px`;
					popoverRef.current.style.left = `${triggerRect.x}px`;
					break;
				}

				case "topRight": {
					popoverRef.current.style.top = `${triggerRect.y - popoverRect.height - triggerRect.height / 4}px`;
					popoverRef.current.style.right = `${width - triggerRect.right}px`;
					break;
				}
			}
		}
	}, [width, height, position]);

	const startCloseTimer = () => {
		if (closeTimeout.current) {
			clearTimeout(closeTimeout.current);
		}

		closeTimeout.current = setTimeout(() => {
			close();
		}, 400);
	};

	const clearCloseTimer = () => {
		if (closeTimeout.current) {
			clearTimeout(closeTimeout.current);
			closeTimeout.current = undefined;
		}
	};

	useEffect(() => {
		if (isOpen) {
			updatePopoverPosition();
		}
	}, [width, height, position, isOpen, updatePopoverPosition]);

	useEffect(() => {
		if (!isOpen) return;

		const handleScroll = () => {
			if (scrollBehavior === "close") {
				close();
			} else {
				updatePopoverPosition();
			}
		};

		const scrollParent = scrollParentRef.current;

		if (scrollParent) {
			scrollParent.addEventListener("scroll", handleScroll, { passive: true });

			return () => {
				scrollParent.removeEventListener("scroll", handleScroll);
			};
		}

		return;
	}, [isOpen, close, scrollBehavior, updatePopoverPosition]);

	const { text: triggerText, ...triggerRest } = trigger;

	return (
		<div className={base({ className })} {...(isOpen ? { "data-open": "" } : {})}>
			<Button
				ref={triggerRef}
				className={triggerClassName({ className: trigger.className })}
				onClick={chain(
					trigger?.onClick,
					!isHoverModeActive && toggleIsOpen,
					stopPropagation,
				)}
				onMouseEnter={isHoverModeActive ? open : undefined}
				onMouseLeave={isHoverModeActive ? startCloseTimer : undefined}
				{...triggerRest}
				tooltip={
					trigger.tooltip
						? {
								...trigger.tooltip,
								hidden: trigger.tooltip?.hidden ?? isOpen,
							}
						: undefined
				}
				leftIcon={selectedItem?.leftIcon ?? trigger.leftIcon}
			>
				{selectedItem ? selectedItem.text : triggerText}
			</Button>

			{typeof rightComponent === "function" ? rightComponent(toggleIsOpen) : rightComponent}

			{createPortal(
				<AnimatePresence>
					{isOpen && (
						<>
							{isWithBackdrop &&
								createPortal(
									<motion.div
										initial={{ opacity: 0 }}
										animate={{ opacity: 0.5 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.4, ease: "easeOut" }}
										className="fixed inset-0 z-10 bg-default backdrop-blur-sm"
									/>,
									document.body,
								)}
							<Popover
								ref={popoverRef}
								{...popover}
								className={popoverClassName({ className: popover.className })}
								style={
									isPopoverWidthFitButton
										? {
												width: triggerRef.current.clientWidth + 2,
											}
										: undefined
								}
								close={close}
								listbox={
									popover.listbox
										? {
												...popover.listbox,
												onClickItem: chain(
													popover.listbox.onClickItem,
													close,
												),
											}
										: undefined
								}
								onMouseLeave={isHoverModeActive ? close : undefined}
								onMouseEnter={isHoverModeActive ? clearCloseTimer : undefined}
							/>
						</>
					)}
				</AnimatePresence>,
				document.body,
			)}
		</div>
	);
}
